import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type {
  CSSProperties,
  ReactNode,
  PointerEvent as ReactPointerEvent,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import type {
  CategoryEntity,
  CategoryGroupEntity,
} from '@actual-app/core/types/models';

import {
  useCreateCategoryGroupMutation,
  useCreateCategoryMutation,
  useDeleteCategoryGroupMutation,
  useDeleteCategoryMutation,
  useMoveCategoryGroupMutation,
  useMoveCategoryMutation,
  useSaveCategoryGroupMutation,
  useSaveCategoryMutation,
  useSortCategoriesMutation,
} from '#budget';
import { MOBILE_NAV_HEIGHT } from '#components/mobile/MobileNavTabs';
import { Boton } from '#components/mobile/ui/Boton';
import { BotonRedondo } from '#components/mobile/ui/Cabecera';
import { Cargando } from '#components/mobile/ui/Cargando';
import { separarEmoji } from '#components/mobile/ui/emoji';
import { HojaAcciones } from '#components/mobile/ui/HojaAcciones';
import type { FilaAccion } from '#components/mobile/ui/HojaAcciones';
import { Icono } from '#components/mobile/ui/Icono';
import type { NombreIcono } from '#components/mobile/ui/Icono';
import {
  color,
  densidad,
  espacio,
  movimiento,
  radio,
  sombra,
  suave,
  TACTIL,
  texto,
} from '#components/mobile/ui/tokens';
import { Page } from '#components/Page';
import { useCategories } from '#hooks/useCategories';
import { useNavigate } from '#hooks/useNavigate';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';

import {
  categoriasVisibles,
  destinoCategoria,
  destinoGrupo,
  filasOcultas,
  gruposVisibles,
  huecoMasCercano,
  mismaPosicionCategoria,
  mismaPosicionGrupo,
  moverCategoriaEnLista,
  moverGrupoEnLista,
} from './editarCategorias';
import type { Hueco, Marca } from './editarCategorias';
import { HojaEditarCategoria } from './HojaEditarCategoria';
import { HojaNuevaCategoria } from './HojaNuevaCategoria';
import { useTravesiaCategoria } from './useTravesiaCategoria';

/** Lo que hay que mantener pulsado para levantar una fila (como Inicio). */
const MANTENER_MS = 450;
/** Si el dedo se mueve más que esto antes, es que quiere desplazar. */
const TOLERANCIA_PX = 8;
/** Margen junto al borde que hace desplazar la lista sola. */
const BORDE_AUTO_PX = 80;
const ALTO_FILA = 52;
const ALTO_GRUPO = 54;

type HojaEditor =
  | { tipo: 'nueva'; modo: 'categoria' | 'grupo'; grupo?: string | null }
  | { tipo: 'editar'; id: string }
  | { tipo: 'menu'; id: string }
  | null;

/** Lo que se está arrastrando y el hueco donde caería (pinta la lista). */
type Arrastre =
  | { tipo: 'categoria'; id: string; hueco: Hueco }
  | { tipo: 'grupo'; id: string; indice: number };

/** Lo que no hace falta pintar: dedo, temporizador, fila flotante. */
type Vivo = {
  pointerId: number;
  x0: number;
  y0: number;
  y: number;
  dy: number;
  temporizador: ReturnType<typeof setTimeout> | null;
  frame: number | null;
  scroll: HTMLElement;
  pendiente: {
    tipo: 'categoria' | 'grupo';
    id: string;
    fila: HTMLElement;
    objetivo: EventTarget | null;
  } | null;
};

function contenedorDesplazable(el: HTMLElement): HTMLElement {
  let p = el.parentElement;
  while (p) {
    const { overflowY } = getComputedStyle(p);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      p.scrollHeight > p.clientHeight
    ) {
      return p;
    }
    p = p.parentElement;
  }
  return (document.scrollingElement as HTMLElement) ?? document.body;
}

function sinMovimiento() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/**
 * «Editar categorías» (Vadik, 13-oct-2026), desde la brújula del Plan: todos
 * los grupos con sus categorías para moverlas (también a otro grupo),
 * cambiar nombre e icono, crear, ocultar y borrar. Lo oculto sale de su
 * grupo y baja a «Ocultas», donde el ojo lo recupera. Arrastre: los seis
 * puntos al momento o mantener pulsada la fila, como las tarjetas de Inicio.
 */
export function EditarCategoriasPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: { grouped } = { list: [], grouped: [] } } = useCategories();

  const createCategory = useCreateCategoryMutation();
  const createCategoryGroup = useCreateCategoryGroupMutation();
  const saveCategory = useSaveCategoryMutation();
  const saveCategoryGroup = useSaveCategoryGroupMutation();
  const moveCategory = useMoveCategoryMutation();
  const moveCategoryGroup = useMoveCategoryGroupMutation();
  const deleteCategory = useDeleteCategoryMutation();
  const deleteCategoryGroup = useDeleteCategoryGroupMutation();
  const sortCategories = useSortCategoriesMutation();

  // Orden recién soltado, hasta que llegue el del servidor.
  const [optimista, setOptimista] = useState<CategoryGroupEntity[] | null>(
    null,
  );
  useEffect(() => setOptimista(null), [grouped]);
  const grupos = optimista ?? grouped ?? [];
  const cargado = !!grouped;

  const [plegados, setPlegados] = useState<ReadonlySet<string>>(new Set());
  const [hoja, setHoja] = useState<HojaEditor>(null);
  const [arrastre, setArrastre] = useState<Arrastre | null>(null);

  const listaRef = useRef<HTMLDivElement>(null);
  const flotaRef = useRef<HTMLDivElement>(null);
  const vivoRef = useRef<Vivo | null>(null);
  // Mientras cancelamos nosotros la pulsación del botón de la fila.
  const cancelandoRef = useRef(false);
  const arrastreRef = useRef<Arrastre | null>(null);
  arrastreRef.current = arrastre;
  const gruposRef = useRef(grupos);
  gruposRef.current = grupos;

  // FLIP: lo que cambia de sitio se desliza en vez de saltar.
  const antesRef = useRef<Map<string, number> | null>(null);
  const capturar = () => {
    const cont = listaRef.current;
    if (!cont || sinMovimiento()) {
      return;
    }
    const mapa = new Map<string, number>();
    cont.querySelectorAll<HTMLElement>('[data-flip]').forEach(el => {
      mapa.set(el.dataset.flip ?? '', el.getBoundingClientRect().top);
    });
    antesRef.current = mapa;
  };
  useLayoutEffect(() => {
    const antes = antesRef.current;
    const cont = listaRef.current;
    if (!antes || !cont) {
      return;
    }
    antesRef.current = null;
    cont.querySelectorAll<HTMLElement>('[data-flip]').forEach(el => {
      const a = antes.get(el.dataset.flip ?? '');
      if (a == null) {
        return;
      }
      const d = a - el.getBoundingClientRect().top;
      if (Math.abs(d) < 1) {
        return;
      }
      el.style.transition = 'none';
      el.style.transform = `translateY(${d}px)`;
      requestAnimationFrame(() => {
        el.style.transition = `transform 200ms ${movimiento.suave}`;
        el.style.transform = '';
      });
    });
  });

  // Coloca la fila flotante bajo el dedo.
  const colocarFlota = () => {
    const v = vivoRef.current;
    const flota = flotaRef.current;
    if (v && flota) {
      flota.style.top = `${v.y - v.dy}px`;
    }
  };

  // Huecos posibles según lo que se ve ahora en pantalla.
  const marcas = (a: Arrastre): Marca[] => {
    const cont = listaRef.current;
    if (!cont) {
      return [];
    }
    const lista = gruposRef.current;
    const res: Marca[] = [];
    if (a.tipo === 'categoria') {
      const origen = lista.find(g => g.categories?.some(c => c.id === a.id));
      cont
        .querySelectorAll<HTMLElement>('[data-grupo-tarjeta]')
        .forEach(tarjeta => {
          const grupo = lista.find(g => g.id === tarjeta.dataset.grupoTarjeta);
          if (!grupo || !!grupo.is_income !== !!origen?.is_income) {
            return;
          }
          const total = categoriasVisibles(grupo, a.id).length;
          if (tarjeta.dataset.plegado === 'true') {
            const r = tarjeta.getBoundingClientRect();
            res.push({ grupoId: grupo.id, indice: total, y: r.top + 12 });
            return;
          }
          // Arriba de cada fila y, al final, justo debajo de la última (o
          // de la cabecera si el grupo está vacío).
          let fin = tarjeta.getBoundingClientRect().top + ALTO_GRUPO;
          tarjeta
            .querySelectorAll<HTMLElement>('[data-fila-categoria]')
            .forEach(fila => {
              const r = fila.getBoundingClientRect();
              res.push({
                grupoId: grupo.id,
                indice: Number(fila.dataset.indice),
                y: r.top,
              });
              fin = r.bottom;
            });
          res.push({ grupoId: grupo.id, indice: total, y: fin });
        });
    } else {
      cont
        .querySelectorAll<HTMLElement>('[data-grupo-tarjeta]')
        .forEach(tarjeta => {
          if (tarjeta.dataset.indiceGrupo == null) {
            return;
          }
          res.push({
            grupoId: '',
            indice: Number(tarjeta.dataset.indiceGrupo),
            y: tarjeta.getBoundingClientRect().top,
          });
        });
      const fin = cont.querySelector<HTMLElement>('[data-fin-grupos]');
      if (fin) {
        res.push({
          grupoId: '',
          indice: res.length,
          y: fin.getBoundingClientRect().top,
        });
      }
    }
    return res;
  };

  const mover = () => {
    const v = vivoRef.current;
    const a = arrastreRef.current;
    if (!v || !a) {
      return;
    }
    colocarFlota();
    const hueco = huecoMasCercano(marcas(a), v.y - v.dy);
    if (!hueco) {
      return;
    }
    if (a.tipo === 'categoria') {
      if (
        hueco.grupoId !== a.hueco.grupoId ||
        hueco.indice !== a.hueco.indice
      ) {
        navigator.vibrate?.(8);
        capturar();
        setArrastre({ ...a, hueco });
      }
    } else if (hueco.indice !== a.indice) {
      navigator.vibrate?.(8);
      capturar();
      setArrastre({ ...a, indice: hueco.indice });
    }
  };

  const autoDesplazar = () => {
    const v = vivoRef.current;
    if (!v || !arrastreRef.current) {
      return;
    }
    const arriba =
      v.scroll === document.scrollingElement
        ? 0
        : v.scroll.getBoundingClientRect().top;
    const abajo = window.innerHeight - MOBILE_NAV_HEIGHT;
    let paso = 0;
    if (v.y < arriba + BORDE_AUTO_PX) {
      paso = -Math.ceil((arriba + BORDE_AUTO_PX - v.y) / 6);
    } else if (v.y > abajo - BORDE_AUTO_PX) {
      paso = Math.ceil((v.y - (abajo - BORDE_AUTO_PX)) / 6);
    }
    if (paso) {
      const antes = v.scroll.scrollTop;
      v.scroll.scrollTop += paso;
      if (v.scroll.scrollTop !== antes) {
        mover();
      }
    }
    v.frame = requestAnimationFrame(autoDesplazar);
  };

  const levantar = () => {
    const v = vivoRef.current;
    if (!v?.pendiente) {
      return;
    }
    const { tipo, id, fila, objetivo } = v.pendiente;
    v.temporizador = null;
    v.pendiente = null;
    // El botón del nombre no debe abrirse al soltar: se cancela su pulsación.
    cancelandoRef.current = true;
    objetivo?.dispatchEvent(
      new PointerEvent('pointercancel', {
        bubbles: true,
        pointerId: v.pointerId,
      }),
    );
    cancelandoRef.current = false;
    const caja = (
      tipo === 'grupo'
        ? (fila.closest<HTMLElement>('[data-grupo-tarjeta]') ?? fila)
        : fila
    ).getBoundingClientRect();
    v.dy = v.y - caja.top;
    navigator.vibrate?.(15);
    capturar();
    const lista = gruposRef.current;
    if (tipo === 'categoria') {
      const grupo = lista.find(g => g.categories?.some(c => c.id === id));
      if (!grupo) {
        return;
      }
      const indice = categoriasVisibles(grupo).findIndex(c => c.id === id);
      setArrastre({
        tipo,
        id,
        hueco: { grupoId: grupo.id, indice },
      });
    } else {
      const indice = lista
        .filter(g => !g.hidden && !g.is_income)
        .findIndex(g => g.id === id);
      setArrastre({ tipo, id, indice });
    }
    v.frame = requestAnimationFrame(autoDesplazar);
  };

  const soltar = () => {
    const v = vivoRef.current;
    const a = arrastreRef.current;
    if (v?.frame != null) {
      cancelAnimationFrame(v.frame);
    }
    vivoRef.current = null;
    if (!a) {
      return;
    }
    const lista = gruposRef.current;
    capturar();
    if (a.tipo === 'categoria') {
      const grupo = lista.find(g => g.id === a.hueco.grupoId);
      if (grupo && !mismaPosicionCategoria(lista, a.id, a.hueco)) {
        const { groupId, targetId } = destinoCategoria(
          grupo,
          a.hueco.indice,
          a.id,
        );
        setOptimista(moverCategoriaEnLista(lista, a.id, groupId, targetId));
        moveCategory.mutate({ id: a.id, groupId, targetId });
      }
    } else if (!mismaPosicionGrupo(lista, a.id, a.indice)) {
      const targetId = destinoGrupo(lista, a.indice, a.id);
      setOptimista(moverGrupoEnLista(lista, a.id, targetId));
      moveCategoryGroup.mutate({ id: a.id, targetId });
    }
    setArrastre(null);
    // El «click» que llega tras soltar no abre nada.
    const bloquear = (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
    };
    window.addEventListener('click', bloquear, true);
    setTimeout(() => window.removeEventListener('click', bloquear, true), 350);
  };

  const soltarRef = useRef(soltar);
  soltarRef.current = soltar;
  const moverRef = useRef(mover);
  moverRef.current = mover;
  const levantarRef = useRef(levantar);
  levantarRef.current = levantar;

  useEffect(() => {
    const cancelarEspera = () => {
      const v = vivoRef.current;
      if (v?.temporizador) {
        clearTimeout(v.temporizador);
      }
      vivoRef.current = null;
    };
    const onMove = (e: PointerEvent) => {
      const v = vivoRef.current;
      if (!v || e.pointerId !== v.pointerId) {
        return;
      }
      v.y = e.clientY;
      if (!arrastreRef.current) {
        if (Math.hypot(e.clientX - v.x0, e.clientY - v.y0) > TOLERANCIA_PX) {
          cancelarEspera();
        }
        return;
      }
      e.preventDefault();
      moverRef.current();
    };
    const onUp = (e: PointerEvent) => {
      const v = vivoRef.current;
      if (cancelandoRef.current || !v || e.pointerId !== v.pointerId) {
        return;
      }
      if (arrastreRef.current) {
        // Nadie más ve este «soltar» (ni el botón de la fila).
        e.stopPropagation();
        e.preventDefault();
        soltarRef.current();
      } else {
        cancelarEspera();
      }
    };
    // Con una fila levantada, el dedo arrastra y no desplaza la página.
    const onTouchMove = (e: TouchEvent) => {
      if (arrastreRef.current && e.cancelable) {
        e.preventDefault();
      }
    };
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', onUp, true);
    window.addEventListener('pointercancel', onUp, true);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      cancelarEspera();
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', onUp, true);
      window.removeEventListener('pointercancel', onUp, true);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, []);

  // Pulsar una fila: desde el asa se levanta al momento; en el resto,
  // tras medio segundo quieto.
  const onPointerDown = (
    e: ReactPointerEvent<HTMLElement>,
    tipo: 'categoria' | 'grupo',
    id: string,
  ) => {
    if (
      vivoRef.current ||
      arrastreRef.current ||
      (e.pointerType === 'mouse' && e.button !== 0)
    ) {
      return;
    }
    const enAsa = (e.target as Element).closest('[data-asa-arrastre]') != null;
    const fila = e.currentTarget;
    vivoRef.current = {
      pointerId: e.pointerId,
      x0: e.clientX,
      y0: e.clientY,
      y: e.clientY,
      dy: 0,
      temporizador: null,
      frame: null,
      scroll: contenedorDesplazable(fila),
      pendiente: { tipo, id, fila, objetivo: e.target },
    };
    if (enAsa) {
      e.preventDefault();
      levantarRef.current();
    } else {
      vivoRef.current.temporizador = setTimeout(
        () => levantarRef.current(),
        MANTENER_MS,
      );
    }
  };

  useLayoutEffect(colocarFlota);

  // Se abre arriba del todo aunque el Plan estuviera desplazado.
  useEffect(() => {
    if (listaRef.current) {
      contenedorDesplazable(listaRef.current).scrollTop = 0;
    }
  }, [cargado]);

  // Travesía de la categoría del menú abierto.
  const categoriaHoja =
    hoja && hoja.tipo !== 'nueva'
      ? (grupos.flatMap(g => g.categories ?? []).find(c => c.id === hoja.id) ??
        null)
      : null;
  const travesiaHoja = useTravesiaCategoria(categoriaHoja);
  const dispatch = useDispatch();
  const abrirHoja = (name: 'zarpar' | 'cerrar-travesia', categoryId: string) =>
    dispatch(
      pushModal({
        modal: {
          name,
          options: { categoryId, month: monthUtils.currentMonth() },
        },
      }),
    );

  if (!grouped) {
    return <Cargando pantalla />;
  }

  const buscar = (id: string) => {
    for (const grupo of grupos) {
      if (grupo.id === id) {
        return { grupo, categoria: null };
      }
      const categoria = grupo.categories?.find(c => c.id === id);
      if (categoria) {
        return { grupo, categoria };
      }
    }
    return null;
  };

  const ocultar = (id: string, oculto: boolean) => {
    const r = buscar(id);
    if (!r) {
      return;
    }
    capturar();
    if (r.categoria) {
      saveCategory.mutate({ category: { ...r.categoria, hidden: oculto } });
    } else {
      saveCategoryGroup.mutate({ group: { ...r.grupo, hidden: oculto } });
    }
  };

  const onCrearGrupo = async (nombre: string): Promise<string | null> => {
    try {
      const id = await createCategoryGroup.mutateAsync({ name: nombre });
      return id ?? null;
    } catch {
      return null;
    }
  };

  const gruposDeTipo = (ingresos: boolean, incluir?: string | null) =>
    grupos.filter(
      g => !!g.is_income === ingresos && (!g.hidden || g.id === incluir),
    );

  const todoPlegado = arrastre?.tipo === 'grupo';
  const visibles = gruposVisibles(grupos);
  const ocultas = filasOcultas(grupos);
  const enArrastre = arrastre ? buscar(arrastre.id) : null;

  const hueco = (alto: number, clave: string, estilo?: CSSProperties) => (
    <View
      key={clave}
      data-flip={clave}
      aria-hidden
      style={{
        height: alto,
        borderRadius: radio.boton,
        border: `1.5px dashed ${color.accent}`,
        backgroundColor: suave(color.accent, 10),
        ...estilo,
      }}
    />
  );

  let indiceGrupo = 0;
  const tarjetas = visibles.map(grupo => {
    if (arrastre?.tipo === 'grupo' && grupo.id === arrastre.id) {
      return null;
    }
    const esIngreso = !!grupo.is_income;
    const plegado = todoPlegado || plegados.has(grupo.id);
    const miIndice = esIngreso ? null : indiceGrupo++;
    const cats = categoriasVisibles(
      grupo,
      arrastre?.tipo === 'categoria' ? arrastre.id : null,
    );
    const huecoAqui =
      arrastre?.tipo === 'categoria' && arrastre.hueco.grupoId === grupo.id
        ? arrastre.hueco.indice
        : null;
    const filas: ReactNode[] = [];
    if (!plegado) {
      cats.forEach((categoria, i) => {
        if (huecoAqui === i) {
          filas.push(hueco(ALTO_FILA - 6, 'hueco', { margin: '3px 8px' }));
        }
        filas.push(
          <FilaCategoria
            key={categoria.id}
            categoria={categoria}
            indice={i}
            onPointerDown={e => onPointerDown(e, 'categoria', categoria.id)}
            onEditar={() => setHoja({ tipo: 'editar', id: categoria.id })}
            onMenu={() => setHoja({ tipo: 'menu', id: categoria.id })}
          />,
        );
      });
      if (huecoAqui != null && huecoAqui >= cats.length) {
        filas.push(hueco(ALTO_FILA - 6, 'hueco', { margin: '3px 8px' }));
      }
    }
    const tarjeta = (
      <View
        key={grupo.id}
        data-flip={grupo.id}
        data-grupo-tarjeta={grupo.id}
        data-plegado={plegado ? 'true' : 'false'}
        data-indice-grupo={miIndice ?? undefined}
        data-testid="editar-grupo"
        style={{
          backgroundColor: color.surface,
          borderRadius: radio.tarjeta,
          boxShadow: sombra.tarjeta,
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <CabeceraGrupo
          grupo={grupo}
          total={categoriasVisibles(grupo).length}
          plegado={plegado}
          fija={esIngreso}
          onPointerDown={e => onPointerDown(e, 'grupo', grupo.id)}
          onEditar={() => setHoja({ tipo: 'editar', id: grupo.id })}
          onPlegar={() => {
            capturar();
            setPlegados(p => {
              const n = new Set(p);
              if (n.has(grupo.id)) {
                n.delete(grupo.id);
              } else {
                n.add(grupo.id);
              }
              return n;
            });
          }}
          onMenu={() => setHoja({ tipo: 'menu', id: grupo.id })}
        />
        {filas}
        {esIngreso && !plegado && (
          <Text
            style={{
              ...texto.secundario,
              color: color.fg3,
              padding: `0 ${espacio.margen}px 10px 46px`,
            }}
          >
            <Trans>Income always goes last.</Trans>
          </Text>
        )}
      </View>
    );
    if (
      arrastre?.tipo === 'grupo' &&
      miIndice != null &&
      miIndice === arrastre.indice
    ) {
      return [hueco(ALTO_GRUPO, 'hueco-grupo'), tarjeta];
    }
    return tarjeta;
  });
  const gastosVisibles = visibles.filter(
    g => !g.is_income && !(arrastre?.tipo === 'grupo' && g.id === arrastre.id),
  ).length;
  const huecoFinal =
    arrastre?.tipo === 'grupo' && arrastre.indice >= gastosVisibles
      ? hueco(ALTO_GRUPO, 'hueco-grupo')
      : null;
  // El hueco del final va delante de Ingresos.
  const primeraIngresos = visibles.findIndex(g => g.is_income);
  const antesDeIngresos =
    primeraIngresos < 0 ? tarjetas : tarjetas.slice(0, primeraIngresos);
  const ingresos = primeraIngresos < 0 ? [] : tarjetas.slice(primeraIngresos);

  const hojaActual = (() => {
    if (!hoja) {
      return null;
    }
    if (hoja.tipo === 'nueva') {
      return (
        <HojaNuevaCategoria
          modo={hoja.modo}
          grupos={gruposDeTipo(
            !!grupos.find(g => g.id === hoja.grupo)?.is_income,
            hoja.grupo,
          )}
          grupoInicial={hoja.grupo}
          onCrear={(nombre, grupo) => {
            if (grupo) {
              createCategory.mutate({
                name: nombre,
                groupId: grupo.id,
                isIncome: !!grupo.is_income,
                isHidden: false,
              });
            } else {
              createCategoryGroup.mutate({ name: nombre });
            }
            setHoja(null);
          }}
          onCrearGrupo={onCrearGrupo}
          onCerrar={() => setHoja(null)}
        />
      );
    }
    const r = buscar(hoja.id);
    if (!r) {
      return null;
    }
    const { grupo, categoria } = r;
    if (hoja.tipo === 'editar') {
      return (
        <HojaEditarCategoria
          id={hoja.id}
          grupos={grupos}
          onCerrar={() => setHoja(null)}
          antesDeGuardar={capturar}
          onMovida={(categoriaId, grupoId) =>
            setOptimista(
              moverCategoriaEnLista(grupos, categoriaId, grupoId, null),
            )
          }
        />
      );
    }
    const { emoji, resto } = separarEmoji(categoria?.name ?? grupo.name);
    const filas: FilaAccion[] = [
      {
        id: 'editar',
        texto: t('Edit name and icon'),
        icono: 'pencil',
        onPress: () => setHoja({ tipo: 'editar', id: hoja.id }),
      },
    ];
    if (!categoria) {
      filas.push({
        id: 'anadir',
        texto: t('Add category'),
        icono: 'plus',
        onPress: () =>
          setHoja({ tipo: 'nueva', modo: 'categoria', grupo: grupo.id }),
      });
      if ((grupo.categories?.length ?? 0) > 1) {
        filas.push({
          id: 'ordenar',
          texto: t('Sort A to Z'),
          icono: 'list',
          onPress: () => {
            setHoja(null);
            capturar();
            sortCategories.mutate({ groupId: grupo.id, direction: 'asc' });
          },
        });
      }
    }
    if (categoria && travesiaHoja.tipo === 'travesia') {
      filas.push(
        {
          id: 'zarpar',
          texto: travesiaHoja.recuerdo ? t('Voyage letter') : t('Set sail'),
          icono: 'velero',
          onPress: () => {
            setHoja(null);
            abrirHoja('zarpar', categoria.id);
          },
        },
        {
          id: 'cerrar-travesia',
          texto: t('Close the voyage'),
          icono: 'compass',
          onPress: () => {
            setHoja(null);
            abrirHoja('cerrar-travesia', categoria.id);
          },
        },
      );
    }
    if (categoria || !grupo.is_income) {
      filas.push(
        {
          id: 'ocultar',
          texto: t('Hide'),
          icono: 'eyeoff',
          onPress: () => {
            setHoja(null);
            ocultar(hoja.id, true);
          },
        },
        {
          id: 'borrar',
          texto: t('Delete'),
          icono: 'trash',
          peligro: true,
          onPress: () => {
            setHoja(null);
            if (categoria) {
              deleteCategory.mutate({ id: categoria.id });
            } else {
              deleteCategoryGroup.mutate({ id: grupo.id });
            }
          },
        },
      );
    }
    return (
      <HojaAcciones
        titulo={resto}
        emoji={emoji}
        filas={filas}
        onCerrar={() => setHoja(null)}
        data-testid="editar-menu"
      />
    );
  })();

  return (
    <Page
      padding={0}
      header={
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: espacio.fila,
            padding: `6px ${espacio.margen}px`,
            minHeight: 56,
          }}
        >
          <BotonRedondo
            icono="cl"
            aria-label={t('Back')}
            onPress={() => void navigate(-1)}
          />
          <Text
            role="heading"
            aria-level={1}
            style={{
              ...texto.titulo,
              flex: 1,
              minWidth: 0,
              color: color.fg,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <Trans>Edit categories</Trans>
          </Text>
          <Button
            variant="bare"
            data-testid="editar-listo"
            onPress={() => void navigate(-1)}
            style={{
              ...texto.fila,
              minHeight: TACTIL,
              padding: '0 10px',
              color: color.accent,
              backgroundColor: 'transparent',
            }}
          >
            <Trans>Finish editing</Trans>
          </Button>
        </View>
      }
    >
      <View
        style={{
          padding: `4px ${espacio.margen - 4}px`,
          paddingBottom: MOBILE_NAV_HEIGHT + espacio.margen,
          gap: 10,
        }}
      >
        <View style={{ flexDirection: 'row', gap: 8, flexShrink: 0 }}>
          <Boton
            data-testid="editar-nueva-categoria"
            onPress={() =>
              setHoja({ tipo: 'nueva', modo: 'categoria', grupo: null })
            }
            style={{ flex: 1, gap: 6 }}
          >
            <Icono nombre="plus" size={18} />
            <Trans>New category</Trans>
          </Boton>
          <Boton
            variante="fantasma"
            data-testid="editar-nuevo-grupo"
            onPress={() => setHoja({ tipo: 'nueva', modo: 'grupo' })}
            style={{
              flex: 1,
              gap: 6,
              backgroundColor: suave(color.accent, 14),
              color: color.accent,
            }}
          >
            <Icono nombre="plus" size={18} />
            <Trans>New category group</Trans>
          </Boton>
        </View>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            padding: '2px 4px 4px',
            flexShrink: 0,
          }}
        >
          <Icono nombre="grip" size={16} style={{ color: color.fg3 }} />
          <Text style={{ ...texto.secundario, color: color.fg3 }}>
            <Trans>
              Drag the dots to move, also to another group. Tap a name to edit
              it.
            </Trans>
          </Text>
        </View>
        <div
          ref={listaRef}
          data-testid="editar-lista"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            flexShrink: 0,
          }}
        >
          {antesDeIngresos}
          {huecoFinal}
          <div data-fin-grupos="" />
          {ingresos}
          {ocultas.length > 0 && (
            <View data-flip="ocultas" style={{ gap: 8, marginTop: 8 }}>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6,
                  padding: '0 6px',
                }}
              >
                <Icono nombre="eyeoff" size={15} style={{ color: color.fg3 }} />
                <Text style={{ ...texto.etiqueta, color: color.fg3 }}>
                  <Trans>Hidden</Trans>
                </Text>
                <Text style={{ ...texto.secundario, color: color.fg3 }}>
                  · <Trans>tap the eye to bring it back</Trans>
                </Text>
              </View>
              <View
                data-testid="editar-ocultas"
                style={{
                  backgroundColor: color.surface,
                  borderRadius: radio.tarjeta,
                  boxShadow: sombra.tarjeta,
                  overflow: 'hidden',
                }}
              >
                {ocultas.map((fila, i) => {
                  const nombre =
                    fila.tipo === 'grupo'
                      ? fila.grupo.name
                      : fila.categoria.name;
                  const id =
                    fila.tipo === 'grupo' ? fila.grupo.id : fila.categoria.id;
                  return (
                    <View
                      key={id}
                      data-testid="editar-oculta"
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 8,
                        minHeight: ALTO_FILA,
                        paddingLeft: espacio.margen,
                        paddingRight: 4,
                        boxShadow: i
                          ? `inset 0 1px 0 ${color.line}`
                          : undefined,
                      }}
                    >
                      <Text
                        style={{
                          ...(fila.tipo === 'grupo'
                            ? densidad.grupo
                            : densidad.nombre),
                          flex: 1,
                          minWidth: 0,
                          color: color.fg3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {nombre}
                      </Text>
                      <Text
                        style={{
                          ...densidad.pequeno,
                          color: color.fg3,
                          maxWidth: 110,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {fila.tipo === 'grupo' ? (
                          <Trans>Group</Trans>
                        ) : (
                          separarEmoji(fila.grupo.name).resto
                        )}
                      </Text>
                      <BotonIcono
                        icono="eyeoff"
                        etiqueta={t('Show again {{name}}', { name: nombre })}
                        testId="editar-mostrar"
                        onPress={() => ocultar(id, false)}
                      />
                    </View>
                  );
                })}
              </View>
            </View>
          )}
        </div>
      </View>
      {arrastre && enArrastre && (
        <div
          ref={flotaRef}
          aria-hidden
          style={{
            position: 'fixed',
            left: espacio.margen - 4,
            right: espacio.margen - 4,
            zIndex: 50,
            pointerEvents: 'none',
            borderRadius: radio.tarjeta,
            overflow: 'hidden',
            backgroundColor: color.surface,
            boxShadow: sombra.flotante,
            transform: 'scale(1.03)',
          }}
        >
          {enArrastre.categoria ? (
            <FilaCategoria categoria={enArrastre.categoria} indice={-1} />
          ) : (
            <CabeceraGrupo
              grupo={enArrastre.grupo}
              total={categoriasVisibles(enArrastre.grupo).length}
              plegado
            />
          )}
        </div>
      )}
      {hojaActual}
    </Page>
  );
}

type BotonIconoProps = {
  icono: NombreIcono;
  etiqueta: string;
  testId?: string;
  onPress?: () => void;
  girado?: boolean;
};

function BotonIcono({
  icono,
  etiqueta,
  testId,
  onPress,
  girado,
}: BotonIconoProps) {
  return (
    <Button
      variant="bare"
      aria-label={etiqueta}
      data-testid={testId}
      onPress={onPress}
      style={{
        width: TACTIL,
        height: TACTIL,
        minWidth: TACTIL,
        padding: 0,
        borderRadius: radio.boton,
        color: color.fg3,
        backgroundColor: 'transparent',
      }}
    >
      <Icono
        nombre={icono}
        size={18}
        style={{
          transition: `transform ${movimiento.pildora}ms ${movimiento.suave}`,
          transform: girado ? 'rotate(-90deg)' : undefined,
        }}
      />
    </Button>
  );
}

function Asa({ etiqueta, apagada }: { etiqueta: string; apagada?: boolean }) {
  return (
    <div
      data-asa-arrastre={apagada ? undefined : ''}
      role="img"
      aria-label={etiqueta}
      style={{
        width: 38,
        height: TACTIL,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color.fg3,
        opacity: apagada ? 0.3 : 1,
        touchAction: apagada ? undefined : 'none',
        cursor: apagada ? 'default' : 'grab',
      }}
    >
      <Icono nombre="grip" size={18} />
    </div>
  );
}

const estiloFila: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
  paddingRight: 4,
  backgroundColor: color.surface,
  userSelect: 'none',
  WebkitUserSelect: 'none',
  WebkitTouchCallout: 'none',
};

type FilaCategoriaProps = {
  categoria: CategoryEntity;
  indice: number;
  onPointerDown?: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onEditar?: () => void;
  onMenu?: () => void;
};

function FilaCategoria({
  categoria,
  indice,
  onPointerDown,
  onEditar,
  onMenu,
}: FilaCategoriaProps) {
  const { t } = useTranslation();
  const { emoji, resto } = separarEmoji(categoria.name);
  return (
    <div
      data-flip={categoria.id}
      data-fila-categoria=""
      data-indice={indice}
      data-testid="editar-categoria"
      onPointerDown={onPointerDown}
      onContextMenu={e => e.preventDefault()}
      style={{
        ...estiloFila,
        minHeight: ALTO_FILA,
        boxShadow: `inset 0 1px 0 ${color.line}`,
      }}
    >
      <Asa etiqueta={t('Move {{name}}', { name: resto })} />
      <Button
        variant="bare"
        aria-label={t('Edit {{name}}', { name: resto })}
        onPress={onEditar}
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: TACTIL,
          justifyContent: 'flex-start',
          gap: 8,
          padding: 0,
          backgroundColor: 'transparent',
          color: color.fg,
        }}
      >
        {emoji && <Text style={densidad.emoji}>{emoji}</Text>}
        <Text
          style={{
            ...densidad.nombre,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {resto}
        </Text>
      </Button>
      <BotonIcono
        icono="more"
        etiqueta={t('More options for {{name}}', { name: resto })}
        testId="editar-mas"
        onPress={onMenu}
      />
    </div>
  );
}

type CabeceraGrupoProps = {
  grupo: CategoryGroupEntity;
  total: number;
  plegado: boolean;
  /** Ingresos: no se mueve. */
  fija?: boolean;
  onPointerDown?: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onEditar?: () => void;
  onPlegar?: () => void;
  onMenu?: () => void;
};

function CabeceraGrupo({
  grupo,
  total,
  plegado,
  fija,
  onPointerDown,
  onEditar,
  onPlegar,
  onMenu,
}: CabeceraGrupoProps) {
  const { t } = useTranslation();
  const { emoji, resto } = separarEmoji(grupo.name);
  return (
    <div
      onPointerDown={fija ? undefined : onPointerDown}
      onContextMenu={e => e.preventDefault()}
      style={{ ...estiloFila, minHeight: ALTO_GRUPO }}
    >
      <Asa etiqueta={t('Move {{name}}', { name: resto })} apagada={fija} />
      <Button
        variant="bare"
        aria-label={t('Edit {{name}}', { name: resto })}
        data-testid="editar-nombre-grupo"
        onPress={onEditar}
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: TACTIL,
          justifyContent: 'flex-start',
          gap: 8,
          padding: 0,
          backgroundColor: 'transparent',
          color: color.fg,
        }}
      >
        {emoji && <Text style={densidad.emoji}>{emoji}</Text>}
        <Text
          style={{
            ...densidad.grupo,
            fontWeight: 800,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {resto}
        </Text>
      </Button>
      <Text style={{ ...densidad.pequeno, color: color.fg3, flexShrink: 0 }}>
        {total}
      </Text>
      <BotonIcono
        icono="cd"
        etiqueta={t('Collapse or expand {{name}}', { name: resto })}
        testId="editar-plegar"
        girado={plegado}
        onPress={onPlegar}
      />
      <BotonIcono
        icono="more"
        etiqueta={t('More options for {{name}}', { name: resto })}
        testId="editar-mas-grupo"
        onPress={onMenu}
      />
    </div>
  );
}
